import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, useTheme, Stack, alpha, IconButton } from '@mui/material';
import { OpenInNew, ChevronLeft, ChevronRight} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import logoImage from '../static/logo-wieland-thermalx-doubleline.png';


// Import product images
const coldPlateImage = require('../static/combined_coldplate_display_remix.png');
const shellTubeImage = require('../static/shell_and_tube.png.webp');
const immersionCoolingImage = require('../static/immersion_cooling_remix.png');
const airCoolerImage = require('../static/air_cooler.png');
const brazePlateImage = require('../static/20250903_1604_Transparent Heat Exchangers_remix_01k47y3z8re3d80w0vt3ehf88j.png');
const specialHeatExchangerImage = require('../static/special_shell_and_tube.png');

interface ProductShowcase {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  category: string;
}

const productShowcases: ProductShowcase[] = [
  {
    id: 'cold-plates',
    title: 'Cold Plates',
    subtitle: 'Advanced Thermal Management',
    description: 'Cold Plates are the ideal solution for dissipating thermal challenges of today\'s technologies. Wieland\'s cold plates significantly boost the performance and lifespan of electronic devices, representing a remarkable advancement in thermal efficiency.',
    image: coldPlateImage,
    category: 'Heat Exchangers',
  },
  {
    id: 'shell-tube',
    title: 'Shell and Tube',
    subtitle: 'Industrial Heat Transfer',
    description: 'We offer a broad range of products in Shell and Tube with customized solutions for almost any requirement: whether it\'s optimizing fluid temperatures or eliminating the oil flow in the discharge gas in industry.',
    image: shellTubeImage,
    category: 'Heat Exchangers',
  },
  {
    id: 'braze-plate',
    title: 'Brazed Plate Heat Exchangers',
    subtitle: 'Compact High-Performance Design',
    description: 'Brazed Plate Heat Exchangers deliver maximum thermal efficiency in a compact footprint. With their robust brazed construction and optimized flow patterns, these units provide superior heat transfer performance for a wide range of industrial applications.',
    image: brazePlateImage,
    category: 'Heat Exchangers',
  },
  {
    id: 'special-heat-exchanger',
    title: 'Special Heat Exchangers',
    subtitle: 'Tailored for Your Needs',
    description: 'Our Special Heat Exchangers are meticulously designed and engineered to meet your unique requirements. These specialized solutions are tailored for your specific needs, delivering optimal performance and efficiency for even the most demanding applications.',
    image: specialHeatExchangerImage,
    category: 'Heat Exchangers',
  },
  {
    id: 'immersion-cooling',
    title: 'Immersion Cooling',
    subtitle: 'Next-Generation Cooling',
    description: 'Immersion Cooling technology delivers unparalleled heat dissipation to meet the demands of high-density heat transfer applications. By immersing components in a bath of non-conductive liquid, this breakthrough solution revolutionizes data center efficiency, cutting energy usage and minimizing the need for conventional methods.',
    image: immersionCoolingImage,
    category: 'Innovation',
  },
  {
    id: 'air-cooler',
    title: 'Air Coolers',
    subtitle: 'Efficient Air-Side Heat Transfer',
    description: 'Air Coolers provide exceptional heat dissipation through optimized air-side heat transfer. These systems are designed for applications requiring efficient cooling with minimal maintenance, offering reliable performance in demanding industrial environments.',
    image: airCoolerImage,
    category: 'Air Cooling',
  },
];

const LoginPage: React.FC = () => {
  const { login, loginB2C } = useAuth();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance slides every 6 seconds for a more relaxed pace
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % productShowcases.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % productShowcases.length);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + productShowcases.length) % productShowcases.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // URL mapping for product links
  const productUrls: { [key: string]: string } = {
    'cold-plates': 'https://www.wieland.com/en/solutions/energy/electronics-cooling/cold-plates',
    'shell-tube': 'https://www.wieland.com/en/solutions/energy/shell-and-tube-heat-exchangers',
    'braze-plate': 'https://www.wieland.com/en/products/heat-exchangers?product-type=brazed-plate-heat-exchangers',
    'special-heat-exchanger': 'https://www.wieland.com/en/products/heat-exchangers',
    'immersion-cooling': 'https://www.wieland.com/en/solutions/energy/electronics-cooling/immersion-cooling',
    'air-cooler': 'https://www.onda-it.com/eng/products/air-heat-exchangers/',
  };

  const handleProductClick = (productId: string) => {
    const url = productUrls[productId];
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAzureB2CLogin = () => {
    loginB2C();
  };

  const handleMicrosoftLogin = () => {
    login();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      {/* Left Side - Product Showcase */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
        background: isDarkMode 
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        }}
      >
        {/* Simplified Hero Product Display */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Hero Image with Consistent Overlay System */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  // Base darkening layer for consistent brightness
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: alpha(theme.palette.common.black, 0.35),
                    zIndex: 1,
                  },
                  // Gradient overlay for visual depth and right-side fade
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(90deg, ${alpha(theme.palette.common.black, 0.2)} 0%, ${alpha(theme.palette.common.black, 0.1)} 40%, ${alpha(theme.palette.common.black, 0.4)} 85%, ${alpha(theme.palette.common.black, 0.7)} 100%)`,
                    zIndex: 2,
                  },
                }}
              >
                <Box sx={{ width: '100%', height: '100%' }}>
                  <Box
                    component="img"
                    src={productShowcases[currentSlide].image}
                    alt={productShowcases[currentSlide].title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                </Box>
              </Box>

              {/* Content Overlay */}
              <Box
                sx={{
                  position: 'relative',
                                      zIndex: 4,
                  color: 'white',
                  textAlign: 'center',
                  maxWidth: '600px',
                  mx: 'auto',
                  px: { xs: 3, sm: 4, md: 6 },
                }}
              >
                {/* Clean Content Layout */}
                <Box sx={{ opacity: 0.95, mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'inline-block',
                      color: alpha(theme.palette.common.white, 0.8),
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em',
                      mb: 3,
                    }}
                  >
                    {productShowcases[currentSlide].category}
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '2.8rem', sm: '3.5rem', md: '4rem', lg: '4.5rem' },
                    mb: 2,
                    letterSpacing: '-0.025em',
                    lineHeight: 0.95,
                    color: 'white',
                  }}
                >
                  {productShowcases[currentSlide].title}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.35rem' },
                    mb: 3,
                    color: alpha(theme.palette.common.white, 0.9),
                    lineHeight: 1.4,
                  }}
                >
                  {productShowcases[currentSlide].subtitle}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    lineHeight: 1.6,
                    mb: 4,
                    color: alpha(theme.palette.common.white, 0.8),
                    maxWidth: '480px',
                    mx: 'auto',
                  }}
                >
                  {productShowcases[currentSlide].description}
                </Typography>

                <Button
                  variant="text"
                  endIcon={<OpenInNew sx={{ fontSize: '1rem' }} />}
                  onClick={() => handleProductClick(productShowcases[currentSlide].id)}
                  sx={{
                    color: 'white',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    px: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      opacity: 0.8,
                    },
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </motion.div>
          </AnimatePresence>

                    {/* Minimal Navigation */}
          <IconButton
            onClick={prevSlide}
            sx={{
              position: 'absolute',
              left: { xs: 20, md: 32 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 4,
              color: alpha(theme.palette.common.white, 0.7),
              width: 40,
              height: 40,
              '&:hover': {
                color: 'white',
                backgroundColor: alpha(theme.palette.common.white, 0.1),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ChevronLeft sx={{ fontSize: '1.5rem' }} />
          </IconButton>

          <IconButton
            onClick={nextSlide}
            sx={{
              position: 'absolute',
              right: { xs: 20, md: 32 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 4,
              color: alpha(theme.palette.common.white, 0.7),
              width: 40,
              height: 40,
              '&:hover': {
                color: 'white',
                backgroundColor: alpha(theme.palette.common.white, 0.1),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ChevronRight sx={{ fontSize: '1.5rem' }} />
          </IconButton>

                    {/* Simple Progress Dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 4,
            }}
          >
            {productShowcases.map((_, index) => (
              <Box
                key={index}
                onClick={() => goToSlide(index)}
                sx={{
                  width: index === currentSlide ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: index === currentSlide 
                    ? theme.palette.common.white 
                    : alpha(theme.palette.common.white, 0.4),
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.8),
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Subtle Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: isDarkMode ? 0.04 : 0.02,
            background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${isDarkMode ? 'ffffff' : '000000'}' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          flex: { xs: 1, lg: '0 0 480px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
          px: { xs: 3, sm: 4, lg: 6 },
          py: { xs: 4, sm: 6 },
          background: {
            xs: `radial-gradient(ellipse at center, ${alpha(theme.palette.primary.main, isDarkMode ? 0.08 : 0.03)} 0%, ${theme.palette.background.default} 50%)`,
            lg: `linear-gradient(90deg, ${alpha(theme.palette.background.default, 0.85)} 0%, ${alpha(theme.palette.background.default, 0.95)} 10%, ${theme.palette.background.default} 25%)`
          },
          minHeight: { xs: '100vh', lg: 'auto' },
        position: 'relative',
          // Subtle pattern overlay
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
            opacity: isDarkMode ? 0.02 : 0.015,
            background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${isDarkMode ? 'ffffff' : '000000'}' fill-opacity='0.4'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          pointerEvents: 'none',
          },
      }}
    >
        <Box sx={{ width: '100%', maxWidth: '360px' }}>
            {/* Logo */}
              <Box 
                component="img"
                src={logoImage}
                alt="Wieland ThermalX Logo"
                sx={{
              height: { xs: '48px', sm: '56px' },
              mb: { xs: 6, sm: 7 },
              filter: isDarkMode ? 'brightness(0.9)' : 'none',
              transition: 'all 0.2s ease',
            }}
          />

          {/* Welcome Text */}
              <Typography 
            variant="h2" 
                component="h1" 
                sx={{ 
              mb: 1.5,
                  fontWeight: 600,
              fontSize: { xs: '1.75rem', sm: '2rem' },
                  color: 'text.primary',
              letterSpacing: '-0.015em',
              lineHeight: 1.25,
                }}
              >
            Welcome back
              </Typography>
            
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
              mb: { xs: 6, sm: 7 },
              fontSize: '1rem',
                  lineHeight: 1.5,
              fontWeight: 400,
                }}
              >
            Sign in to continue to ThermalX
              </Typography>

            {/* Login Buttons */}
            <Stack spacing={3}>
              {/* Primary Login Button - External Users */}
                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ 
                    py: 2.5,
                    fontSize: '1rem',
                      fontWeight: 500,
                      borderRadius: 2,
                      textTransform: 'none',
                    background: theme.palette.primary.main,
                    color: 'white',
                    border: 'none',
                    boxShadow: 'none',
                      '&:hover': { 
                      background: theme.palette.primary.dark,
                      boxShadow: 'none',
                      },
                      '&:active': {
                      boxShadow: 'none',
                      },
                    transition: 'all 0.15s ease',
                    }}
                    onClick={handleAzureB2CLogin}
                  >
                  Login / Register
                  </Button>
                
              {/* Divider */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                my: 0.5
                }}>
                  <Box sx={{ 
                    flex: 1, 
                  height: '1px', 
                  backgroundColor: 'divider'
                }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.8rem',
                    fontWeight: 400,
                    px: 1,
                    opacity: 0.7,
                  }}
                >
                  or login with
                  </Typography>
                  <Box sx={{ 
                    flex: 1, 
                  height: '1px', 
                  backgroundColor: 'divider'
                  }} />
                </Box>

              {/* Employee Social Login - Smaller */}
                  <Button
                    type="button"
                    fullWidth
                variant="text"
                size="medium"
                    onClick={handleMicrosoftLogin}
                    sx={{
                  py: 1.5,
                  fontSize: '0.9rem',
                      fontWeight: 500,
                      borderRadius: 2,
                      textTransform: 'none',
                  color: 'text.secondary',
                  backgroundColor: 'transparent',
                  border: isDarkMode ? `1px solid ${alpha(theme.palette.common.white, 0.08)}` : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                  '&:hover': {
                    backgroundColor: isDarkMode ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.common.black, 0.04),
                    borderColor: isDarkMode ? alpha(theme.palette.common.white, 0.12) : alpha(theme.palette.common.black, 0.12),
                      color: 'text.primary',
                  },
                  transition: 'all 0.15s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box
                        component="img"
                        src={process.env.PUBLIC_URL + '/wieland_w.gif'}
                    alt="Wieland"
                        sx={{
                          width: 20,
                          height: 20,
                      filter: isDarkMode ? 'brightness(0.8)' : 'brightness(1.1)',
                      opacity: 0.8,
                        }}
                      />
                    </Box>
                  </Button>
              </Stack>

                    {/* Footer Text */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            align="center" 
            sx={{ 
              mt: 6, 
              lineHeight: 1.4,
              fontSize: '0.85rem',
              opacity: 0.7,
            }}
          >
            You will be redirected to the provider's login page to securely authenticate your account.
          </Typography>

        </Box>

        {/* Copyright - Centered at Bottom of Right Section */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          align="center" 
          sx={{ 
            position: 'absolute',
            bottom: 24,
            left: 0,
            right: 0,
            fontSize: '0.75rem',
            opacity: 0.6,
          }}
        >
          © 2025 ThermalX. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
